<template>
  <div class="validation-modal-overlay">
    <div class="validation-modal">
      <div class="modal-header">
        <h2 class="title">
          {{ $t('components.reoffer_modal.title') }}
        </h2>
        <p class="subtitle">
          {{ $t('components.reoffer_modal.subtitle') }}
        </p>
      </div>

      <div class="modal-content d-flex justify-space-between">
        <div class="flex-column">
          <div class="offers">
            <strong class="d-block mb-4">{{ $t('components.reoffer_modal.offers') }}</strong>
            <div class="offers-list">
              <div
                v-for="(offer, index) in offersPayload"
                :key="offer.id"
                class="offer-item d-flex align-center mb-3 offer-item-reoffer"
                :data-content="$t('components.reoffer_modal.status.undo')"
                @click="removeOffer(index)"
              >
                <component
                  :is="getIconByMaterialType(mountMaterial(creditOffered.material_type), false)"
                  class="material-type-img"
                />
                <div class="d-flex flex-column ml-4">
                  <p class="ma-0">
                    <strong>{{ $t('components.reoffer_modal.unit_value') }}</strong> {{ formatMoney(offer.unit_value) }}
                  </p>
                  <p class="ma-0">
                    <strong>{{ $t('components.reoffer_modal.quantity') }}</strong> {{ `${offer.quantity}T` }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex space flex-column mt-4">
            <strong>{{ $t('components.reoffer_modal.total_quantity') }}</strong>
            <p :class="['total-credits', mountMaterial(creditOffered.material_type)]">
              {{ $t('components.reoffer_modal.credits_sum', { quantity: creditsSum }) }}
            </p>
          </div>
        </div>

        <div class="flex-column">
          <div class="d-flex space flex-column">
            <strong class="d-block mb-4">{{ $t('components.reoffer_modal.invoices', { quantity: nfesAvailable.length }) }}</strong>

            <div class="content-nfes">
              <div
                v-for="nfe in nfesAvailable"
                :key="nfe.id"
              >
                <v-checkbox
                  :color="`${mountMaterial(creditOffered.material_type)}_color`"
                  hide-details
                  :value="nfe.id"
                  @click="toggleSelected(nfe.id)"
                >
                  <template #label>
                    <div>
                      <div>{{ nfe.identification }}</div>
                      <div class="d-flex flex-column">
                        <span>
                          {{ $t('components.reoffer_modal.date') }} {{ (nfe.issue_date) }}
                        </span>
                        <span>
                          {{ $t('components.reoffer_modal.quantity') }} {{ nfe.quantity }}T
                        </span>
                      </div>
                    </div>
                  </template>
                </v-checkbox>
              </div>
            </div>

            <div class="actions d-flex align-center justify-end">
              <input
                v-model="unitValue"
                v-mask="['##', '#,##', '##,##', '###,##']"
                type="text"
                :placeholder="$t('components.reoffer_modal.unit_value_placeholder')"
                class="field-offered"
              >

              <rectoplus-button
                class="close-button"
                :text="$t('components.reoffer_modal.add')"
                height="40px"
                :disabled="!unitValue || !nfeIds.length"
                @click="addNewOffer"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <rectoplus-button
          class="close-button"
          :text="$t('components.dialogs.validation.close')"
          :outlined="true"
          @click="$emit('close')"
        />

        <rectoplus-button
          class="close-button"
          :text="$t('components.reoffer_modal.confirm')"
          :disabled="!!nfesAvailable.length"
          @click="$emit('confirm', params)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import RectoplusButton from '@/components/buttons/RectoplusButton.vue';
import formatMoney from '@/mixins/formatMoney';
import getIconsByMaterialType from '@/mixins/getIconsOfMaterialType';
import reofferMixin from '@/mixins/reofferMixin';

export default {
  name: 'CreateReofferModal',
  components: {
    RectoplusButton,
  },
  mixins: [formatMoney, getIconsByMaterialType, reofferMixin],
  props: {
    creditOffered: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      creditsSum: 0.0,
      unitValue: null,
      offersPayload: [],
      nfeIds: [],
      nfesAvailable: [],
    };
  },
  computed: {
    ...mapGetters(['getNfesToReoffer']),

    params() {
      return { offereds: this.offersPayload };
    },
  },
  methods: {
    ...mapActions(['fetchNfesToReoffer']),

    removeOffer(index) {
      this.offersPayload.splice(index, 1);

      this.nfesAvailable = this.getNfesToReoffer.filter((nfe) => !this.offersPayload.some((offer) => offer.nfe_ids.includes(nfe.id)));
      this.calculateCreditsSum();
    },

    addNewOffer() {
      const nfes = this.getNfesToReoffer.filter((nfe) => this.nfeIds.includes(nfe.id));
      const newOffer = this.formatNewOffer(nfes, this.unitValue);
      this.offersPayload.push(newOffer);
      this.updateNfesAvailable();
      this.resetForm();
    },
  },

  watch: {
    getNfesToReoffer: {
      handler(newVal) {
        const params = newVal.map((nfe) => nfe);

        this.nfesAvailable = params;
      },
    },
  },

  mounted() {
    const params = this.getNfesToReoffer.map((nfe) => nfe);

    this.nfesAvailable = params;
  },
};
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/components/modal/reoffer_modal.scss';
</style>
